import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    Container,
    Typography,
    Button,
    Box,
    Chip,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    IconButton
} from '@mui/material';
import {
    Business as BusinessIcon,
    Group as GroupIcon,
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../context/UserContext';
import AccessControlService from '../../services/AccessControlService';
import {
    Clinic,
    Cohort
} from '../../interfaces/Clinic';
import {
    UserOption,
    getUserDisplayName
} from '../../interfaces/accessControl/AccessControl';

const UserClinicCohortAccessPage: React.FC = () => {
    const { t } = useTranslation();
    const userCtx = useContext(UserContext);
    const [cookies] = useCookies(['access_token']);
    
    // Check if user has permission to access this page
    const hasPermission = userCtx?.user?.role?.roletype === 'orgadmin' || 
                         userCtx?.user?.role?.roletype === 'domainadmin' || 
                         userCtx?.user?.role?.roletype === 'serveradmin';

    const [users, setUsers] = useState<UserOption[]>([]);
    const [clinics, setClinics] = useState<Clinic[]>([]);
    const [cohorts, setCohorts] = useState<Cohort[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    
    // Current assignments data
    const [clinicAccessRecords, setClinicAccessRecords] = useState<any[]>([]);
    const [cohortAccessRecords, setCohortAccessRecords] = useState<any[]>([]);
    
    // Edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<UserOption | null>(null);
    const [selectedClinics, setSelectedClinics] = useState<number[]>([]);
    const [selectedCohorts, setSelectedCohorts] = useState<number[]>([]);
    // Access levels removed: membership only
    const [saving, setSaving] = useState(false);
    
    // Tree expansion state
    const [expandedClinics, setExpandedClinics] = useState<Set<number>>(new Set());

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        
        try {
            const [usersData, clinicsData, cohortsData] = await Promise.all([
                AccessControlService.getEligibleUsers(cookies.access_token),
                AccessControlService.getClinics(cookies.access_token),
                AccessControlService.getCohorts(cookies.access_token)
            ]);
            
            setUsers(Array.isArray(usersData) ? usersData : []);
            setClinics(Array.isArray(clinicsData) ? clinicsData : []);
            // Ensure all cohorts have a valid id property
            if (Array.isArray(cohortsData)) {
                const normalizedCohorts = cohortsData
                    .map((c: any) => ({
                        ...c,
                        id: typeof c.id === 'number' && c.id > 0 ? c.id : (typeof c.cohort_id === 'number' && c.cohort_id > 0 ? c.cohort_id : undefined)
                    }))
                    .filter(c => typeof c.id === 'number' && c.id > 0);
                setCohorts(normalizedCohorts);
            } else {
                setCohorts([]);
            }
            
            // Now fetch access records using the clinics we just loaded
            if (Array.isArray(clinicsData)) {
                const clinicAccessPromises = clinicsData.map(clinic => 
                    AccessControlService.getClinicAccess(clinic.id, cookies.access_token)
                );
                const [clinicAccessData, cohortAccessData] = await Promise.all([
                    Promise.all(clinicAccessPromises),
                    AccessControlService.getCohortAccess(cookies.access_token)
                ]);
                
                const allClinicAccess = clinicAccessData.flat();
                setClinicAccessRecords(allClinicAccess);
                setCohortAccessRecords(Array.isArray(cohortAccessData) ? cohortAccessData : []);
                // Logging after data loads
                console.log('Loaded clinicAccessRecords:', allClinicAccess);
                console.log('Loaded cohortAccessRecords:', cohortAccessData);
            }
        } catch (err: any) {
            console.error('Error loading data:', err);
            setError(`Failed to load data: ${err.message}`);
            setUsers([]);
            setClinics([]);
            setCohorts([]);
            setClinicAccessRecords([]);
            setCohortAccessRecords([]);
        } finally {
            setLoading(false);
        }
    }, [cookies.access_token]);

    useEffect(() => {
        if (hasPermission) {
            loadData();
        }
    }, [hasPermission, loadData]);

    // Initialize dialog state when editing user changes
    useEffect(() => {
        if (editingUser && editDialogOpen) {
            // Logging for dialog state initialization
            console.log('Dialog initializing for user:', editingUser);
            console.log('Current clinicAccessRecords:', clinicAccessRecords);
            console.log('Current cohortAccessRecords:', cohortAccessRecords);
            // Initialize current access for this user
            const userClinicAccess = clinicAccessRecords.filter(access => 
                access.user_id === editingUser.id && access.is_active
            );
            const userCohortAccess = cohortAccessRecords.filter(access => 
                access.user_id === editingUser.id && access.is_active
            );
            console.log('Filtered userClinicAccess:', userClinicAccess);
            console.log('Filtered userCohortAccess:', userCohortAccess);
            // Set selected clinics (support both clinic_id and clinic field)
            const selectedClinicIds = userClinicAccess.map(access => access.clinic_id ?? access.clinic);
            setSelectedClinics(selectedClinicIds);

            // Set selected cohorts
            const selectedCohortIds = userCohortAccess.map(access => access.cohort_id);
            setSelectedCohorts(selectedCohortIds);

            // Expand clinics that have selected cohorts
            const clinicsToExpand = new Set<number>();
            selectedCohortIds.forEach(cohortId => {
                const cohort = cohorts.find(c => c.id === cohortId);
                if (cohort) {
                    clinicsToExpand.add(cohort.clinic_id);
                }
            });
            setExpandedClinics(clinicsToExpand);
        }
    }, [editingUser, editDialogOpen, clinicAccessRecords, cohortAccessRecords, cohorts]);

    const handleUserRowClick = (user: UserOption) => {
        // Only set editing user and open dialog; state will be initialized by useEffect
        setEditingUser(user);
        setEditDialogOpen(true);
    };

    const handleClinicToggle = (clinicId: number) => {
        const clinicCohorts = cohorts.filter(c => c.clinic_id === clinicId);
        const cohortIds = clinicCohorts.map(c => c.id);
        const allSelected = cohortIds.every(id => selectedCohorts.includes(id));

        if (allSelected) {
            // Unselect all cohorts in this clinic
            setSelectedCohorts(prev => prev.filter(id => !cohortIds.includes(id)));
        } else {
            // Select all cohorts in this clinic
            setSelectedCohorts(prev => Array.from(new Set([...prev, ...cohortIds])));
        }
    };

    const handleCohortToggle = (cohortId: number) => {
        setSelectedCohorts(prev =>
            prev.includes(cohortId)
                ? prev.filter(id => id !== cohortId)
                : [...prev, cohortId]
        );
    };

    // Access level change handlers removed

    const handleClinicExpandToggle = (clinicId: number) => {
        setExpandedClinics(prev => {
            const newSet = new Set(prev);
            if (newSet.has(clinicId)) {
                newSet.delete(clinicId);
            } else {
                newSet.add(clinicId);
            }
            return newSet;
        });
    };

    const handleSaveAccess = async () => {
        if (!editingUser) return;

        setSaving(true);
        setError('');

        try {
            const grantedById = userCtx?.user?.user_id;
            if (!grantedById) {
                setError('Unable to determine current user for granting access');
                return;
            }

            const promises: Promise<any>[] = [];

            // Handle clinic access changes
            for (const clinic of clinics) {
                const isSelected = selectedClinics.includes(clinic.id);
                const currentAccess = clinicAccessRecords.find(access => 
                    access.user_id === editingUser.id && access.clinic === clinic.id && access.is_active
                );
                // Access level logic removed

                if (isSelected && currentAccess) {
                    // Update existing access if level changed
                    // Removed access level update logic for clinics
                } else if (isSelected && !currentAccess) {
                    // Grant new access
                    promises.push(
                        AccessControlService.grantClinicAccess(clinic.id, {
                            clinic_id: clinic.id,
                            user_id: editingUser.id,
                            access_level: 'read',
                            granted_by_id: grantedById
                        }, cookies.access_token)
                    );
                } else if (!isSelected && currentAccess) {
                    // Revoke access
                    promises.push(
                        AccessControlService.revokeClinicAccess(currentAccess.id, cookies.access_token)
                    );
                }
            }

            // Handle cohort access changes
            for (const cohort of cohorts) {
                const isSelected = selectedCohorts.includes(cohort.id);
                const currentAccess = cohortAccessRecords.find(access => 
                    access.user_id === editingUser.id && access.cohort_id === cohort.id && access.is_active
                );
                // Access level logic removed

                if (isSelected && currentAccess) {
                    // Update existing access if level changed
                    // Removed access level update logic for cohorts
                } else if (isSelected && !currentAccess) {
                    // Grant new access
                    promises.push(
                        AccessControlService.grantCohortAccess({
                            user_id: editingUser.id,
                            cohort_id: cohort.id, // <-- fix here
                            access_level: 'read',
                            granted_by_id: grantedById
                        }, cookies.access_token)
                    );
                } else if (!isSelected && currentAccess) {
                    // Revoke access
                    promises.push(
                        AccessControlService.revokeCohortAccess(currentAccess.id, cookies.access_token)
                    );
                }
            }

            await Promise.all(promises);
            
            setEditDialogOpen(false);
            setEditingUser(null);
            setSelectedClinics([]);
            setSelectedCohorts([]);
            // Access levels removed
            setExpandedClinics(new Set());
            
            // Reload data to reflect changes
            await loadData();
            
        } catch (err: any) {
            setError(`Failed to save access changes: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    // Ensure clinics can be matched even if API omits ids
    const getClinicIdentifier = (access: any, fallback: string) => {
        const rawId = access?.clinic_id ?? access?.clinic ?? access?.clinic?.id ?? access?.clinic?.clinic_id;
        if (rawId !== undefined && rawId !== null) {
            return `clinic-${rawId}`;
        }
        const name = access?.clinic_name ?? access?.clinic?.name;
        if (name) {
            return `clinic-name-${name}`;
        }
        return fallback;
    };

    const findClinicNameById = (clinicId: number | string | undefined) => {
        if (clinicId === undefined || clinicId === null) return '';
        const numericId = Number(clinicId);
        const clinicMatch = clinics.find(
            clinic => clinic.id === numericId || (clinic as any).clinic_id === numericId
        );
        return clinicMatch?.name ?? '';
    };

    const findClinicNameFromCohort = (cohortId: number | string | undefined) => {
        if (cohortId === undefined || cohortId === null) return '';
        const cohortMatch = cohorts.find(
            cohort => cohort.id === cohortId || (cohort as any).cohort_id === cohortId
        );
        if (!cohortMatch) return '';
        const clinicId = (cohortMatch as any).clinic_id ?? (cohortMatch as any).clinic ?? (cohortMatch as any).clinicId;
        return findClinicNameById(clinicId);
    };

    const findCohortNameById = (cohortId: number | string | undefined) => {
        if (cohortId === undefined || cohortId === null) return '';
        const cohortMatch = cohorts.find(
            cohort => cohort.id === cohortId || (cohort as any).cohort_id === cohortId
        );
        return cohortMatch?.name ?? '';
    };

    const renderCurrentAssignments = () => {
        // Group access records by user
        const userAssignments: { [key: number]: { user: UserOption, clinics: any[], cohorts: any[] } } = {};

        // Initialize all users
        users.forEach(user => {
            userAssignments[user.id] = { user, clinics: [], cohorts: [] };
        });

        // Add clinic access
        clinicAccessRecords.forEach(access => {
            if (userAssignments[access.user_id] && access.is_active) {
                userAssignments[access.user_id].clinics.push(access);
            }
        });

        // Add cohort access
        cohortAccessRecords.forEach(access => {
            if (userAssignments[access.user_id] && access.is_active) {
                userAssignments[access.user_id].cohorts.push(access);
            }
        });

        const assignments = Object.values(userAssignments).map(({ user, clinics, cohorts }) => {
            const clinicMap = new Map<string, { key: string; clinicName: string; cohorts: string[] }>();

            const upsertClinic = (identifier: string, clinicName: string) => {
                const existing = clinicMap.get(identifier);
                if (existing) {
                    if (!existing.clinicName && clinicName) {
                        existing.clinicName = clinicName;
                    }
                    return existing;
                }
                const entry = {
                    key: identifier,
                    clinicName: clinicName || 'Unknown clinic',
                    cohorts: [] as string[]
                };
                clinicMap.set(identifier, entry);
                return entry;
            };

            clinics.forEach((access, index) => {
                const identifier = getClinicIdentifier(access, `clinic-${user.id}-${index}`);
                const clinicName = access?.clinic_name || findClinicNameById(access?.clinic_id ?? access?.clinic) || 'Unknown clinic';
                upsertClinic(identifier, clinicName);
            });

            cohorts.forEach((access, index) => {
                const identifier = getClinicIdentifier(access, `cohort-clinic-${user.id}-${index}`);
                const clinicName = access?.clinic_name || findClinicNameFromCohort(access?.cohort_id) || 'Unknown clinic';
                const entry = upsertClinic(identifier, clinicName);
                const cohortName = access?.cohort_name || findCohortNameById(access?.cohort_id) || `Cohort ${access?.cohort_id ?? index + 1}`;
                if (!entry.cohorts.includes(cohortName)) {
                    entry.cohorts.push(cohortName);
                }
            });

            const clinicSummaries = Array.from(clinicMap.values()).sort((a, b) => a.clinicName.localeCompare(b.clinicName));
            return { user, clinicSummaries };
        });

        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    {t('user_access_assignments')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('click_user_to_edit')}
                </Typography>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('User')}</TableCell>
                                <TableCell>{t('Role')}</TableCell>
                                <TableCell>{t('assigned_clinics_cohorts')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {assignments.map(({ user, clinicSummaries }) => (
                                <TableRow 
                                    key={user.id} 
                                    hover 
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => handleUserRowClick(user)}
                                >
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {getUserDisplayName(user)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {user.username}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={user.role?.roletype || 'No role'} 
                                            size="small" 
                                            color="primary" 
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ width: '40%' }}>
                                        {clinicSummaries.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                {clinicSummaries.map((summary) => (
                                                    <Box
                                                        key={summary.key}
                                                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                                                    >
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {summary.clinicName}
                                                        </Typography>
                                                        {summary.cohorts.length > 0 && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {`– ${summary.cohorts.join(', ')}`}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {t('no_clinic_cohort_access')}
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    if (!hasPermission) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">
                    {t('permission_denied_clinic_access')}
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                {t('user_clinic_cohort_access')}
            </Typography>
            
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            
            {renderCurrentAssignments()}

            {/* Edit Access Dialog */}
            <Dialog 
                open={editDialogOpen} 
                onClose={() => setEditDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { display: 'flex', flexDirection: 'column', maxHeight: '80vh' }
                }}
            >
                <DialogTitle>
                    {t('edit_access_for')} {editingUser ? getUserDisplayName(editingUser) : ''}
                </DialogTitle>
                <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {t('select_clinics_cohorts')}
                    </Typography>

                    <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                        {clinics.map((clinic) => {
                            // Compute selection state for clinic based on its cohorts
                            // Only use cohorts with a valid id
                            const clinicCohorts = cohorts.filter(c => c.clinic_id === clinic.id && typeof c.id === 'number' && c.id > 0);
                            const selectedCohortCount = clinicCohorts.filter(c => selectedCohorts.includes(c.id)).length;
                            const allCohortsSelected = clinicCohorts.length > 0 && selectedCohortCount === clinicCohorts.length;
                            const someCohortsSelected = selectedCohortCount > 0 && selectedCohortCount < clinicCohorts.length;
                            const isClinicSelected = allCohortsSelected;
                            const isClinicIndeterminate = someCohortsSelected;
                            const isExpanded = expandedClinics.has(clinic.id);

                            // Logging for debugging
                            if (editingUser) {
                                console.log(`Clinic: ${clinic.name} (ID: ${clinic.id}) | User: ${editingUser.username} | isClinicSelected: ${isClinicSelected} | isClinicIndeterminate: ${isClinicIndeterminate}`);
                                const userClinicAccess = clinicAccessRecords.filter(access => access.user_id === editingUser.id && access.clinic_id === clinic.id);
                                console.log('User clinic access records:', userClinicAccess);
                            }

                            return (
                                <Box key={clinic.id} sx={{ mb: 1 }}>
                                    {/* Clinic Row */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                        <IconButton 
                                            size="small" 
                                            onClick={() => handleClinicExpandToggle(clinic.id)}
                                            sx={{ mr: 1 }}
                                        >
                                            {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                                        </IconButton>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={isClinicSelected}
                                                    indeterminate={isClinicIndeterminate}
                                                    onChange={() => handleClinicToggle(clinic.id)}
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                                                        {clinic.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {clinicCohorts.length} {t('cohorts')}
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{ flex: 1 }}
                                        />
                                        {/* Remove access level chips for clinics */}
                                        {false && isClinicSelected && (
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {/* Access level chips removed */}
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Cohort Rows */}
                                    <Collapse in={isExpanded}>
                                        <Box sx={{ ml: 4, mt: 1 }}>
                                            {clinicCohorts.map((cohort) => {
                                                // Only render cohorts with a valid id (already filtered above)
                                                if (typeof cohort.id !== 'number' || cohort.id <= 0) return null;
                                                const isCohortSelected = selectedCohorts.includes(cohort.id);
                                                // Logging for debugging
                                                if (editingUser) {
                                                    console.log(`Cohort: ${cohort.name} (ID: ${cohort.id}) | User: ${editingUser.username} | isCohortSelected: ${isCohortSelected}`);
                                                    const userCohortAccess = cohortAccessRecords.filter(access => access.user_id === editingUser.id && access.cohort_id === cohort.id);
                                                    console.log('User cohort access records:', userCohortAccess);
                                                }
                                                return (
                                                    <Box key={`cohort-${cohort.id}`} sx={{ display: 'flex', alignItems: 'center', p: 1, border: '1px solid #f0f0f0', borderRadius: 1, mb: 0.5 }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={isCohortSelected}
                                                                    onChange={() => handleCohortToggle(cohort.id)}
                                                                />
                                                            }
                                                            label={
                                                                <Typography variant="body2">
                                                                    <GroupIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1rem' }} />
                                                                    {cohort.name}
                                                                </Typography>
                                                            }
                                                            sx={{ flex: 1 }}
                                                        />
                                                        {/* Remove access level chips for cohorts */}
                                                        {false && isCohortSelected && (
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                {/* Access level chips removed */}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>
                        {t('cancel')}
                    </Button>
                    <Button 
                        onClick={handleSaveAccess}
                        variant="contained"
                        disabled={saving}
                    >
                        {saving ? <CircularProgress size={20} /> : t('Save Changes')}
                    </Button>
                </DialogActions>
            </Dialog>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <CircularProgress />
                </Box>
            )}
        </Container>
    );
};

export default UserClinicCohortAccessPage;
